/* ============================================================
   SOLVEY QUIZ — v3 (claim-free routine model)
   ------------------------------------------------------------
   - Flow ported from Fleur's SolveyQuizNew prototype:
     intro → segment → segIntro → per-segment question(s)
     → philosophy → result (routine cards + sub-toggle + bundle)
   - NO medical / health claims. Everything is framed as a
     "routine / ritual". All copy comes from Liquid (Theme Editor
     + Weglot-translatable via the [data-sq-i18n] DOM mirror).
   - Real cart-add wired to /cart/add.js → redirect to /cart.
     Restore Probiotics is added with the Firmhouse selling plan
     when the subscription toggle is on.
   ============================================================ */
(function () {
  'use strict';

  function init(root) {
    if (root.__sqInitV3) return;
    root.__sqInitV3 = true;

    var configEl = root.querySelector('script[data-sq-config]');
    if (!configEl) return;
    var config;
    try { config = JSON.parse(configEl.textContent); } catch (e) {
      console.error('[solvey-quiz] invalid config', e);
      return;
    }

    var app           = root.querySelector('[data-sq-app]');
    var progressFill  = root.querySelector('[data-sq-progress-fill]');
    var progressLabel = root.querySelector('[data-sq-progress-label]');

    var P = config.products || {};
    var B = config.bundles || {};

    /* ============================================================
       COPY — JSON config is the English fallback; the [data-sq-i18n]
       DOM mirror is server-side translated by Weglot. Overlay the
       mirror values over the JSON so the quiz follows the active
       language without Weglot needing to read <script> JSON.
       ============================================================ */
    function setPath(obj, path, val) {
      var parts = path.split('.'), n = obj, i;
      for (i = 0; i < parts.length - 1; i++) {
        if (typeof n[parts[i]] !== 'object' || n[parts[i]] == null) n[parts[i]] = {};
        n = n[parts[i]];
      }
      n[parts[parts.length - 1]] = val;
    }
    function buildCopy() {
      var base;
      try { base = JSON.parse(JSON.stringify(config.copy || {})); } catch (e) { base = {}; }
      var box = root.querySelector('[data-sq-i18n]');
      if (box) {
        box.querySelectorAll('[data-k]').forEach(function (el) {
          var k = el.getAttribute('data-k');
          var v = el.innerHTML.trim();
          if (!k || v === '' || k.indexOf('framing.') === 0) return;
          setPath(base, k, v);
        });
      }
      return base;
    }
    var cp = buildCopy();

    function t(path, fallback) {
      var parts = path.split('.');
      var node = cp;
      for (var i = 0; i < parts.length; i++) {
        if (node == null) return fallback || '';
        node = node[parts[i]];
      }
      return (node == null || node === '') ? (fallback || '') : node;
    }

    /* framing string is built from a key → translated via the mirror */
    function framing(key) {
      if (!key) return '';
      var box = root.querySelector('[data-sq-i18n]');
      if (box) {
        var el = box.querySelector('[data-k="framing.' + key + '"]');
        if (el && el.textContent.trim()) return el.textContent.trim();
      }
      return t('framing.' + key, '');
    }

    /* comma / newline separated list → array of trimmed strings */
    function listOf(path) {
      var raw = t(path, '');
      if (!raw) return [];
      return raw.split(/[\n,]+/).map(function (s) { return s.trim(); }).filter(Boolean);
    }

    function fmtPrice(cents) {
      if (cents == null) return '€0,00';
      return '€' + (Math.round(cents) / 100).toFixed(2).replace('.', ',');
    }

    /* ============================================================
       PRODUCTS — real variant/price/image from Liquid, claim-free
       desc/meta from copy. probiotics carries subscribe pricing.
       ============================================================ */
    function product(key, roleKey) {
      var src = P[key];
      if (!src) return null;
      var oneTime = src.price_cents || 0;
      var obj = {
        key: key,
        roleKey: roleKey,
        name: src.title || '',
        url: src.url || '#',
        variant_id: src.variant_id,
        price_cents: oneTime,
        image: src.image,
        desc: t('products.' + key + '.desc', ''),
        meta: t('products.' + key + '.meta', '')
      };
      // Any product Shopify exposes a real subscription allocation for becomes
      // subscribable (price + plan id come straight from Liquid). The display
      // still defaults to one-time pricing; the per-card toggle opts in.
      if (src.price_subscribe_cents && src.sub_plan_id) {
        obj.sub = true;
        obj.price_subscribe_cents = src.price_subscribe_cents;
        obj.sub_plan_id = src.sub_plan_id;
      }
      return obj;
    }

    function bundleInfo(key) {
      var src = B[key];
      if (!src || !src.variant_id) return null;
      var parts = key.split('+');
      var sum = 0;
      parts.forEach(function (k) { sum += (P[k] && P[k].price_cents) || 0; });
      var bundlePrice = src.price_cents || 0;
      return {
        key: key,
        name: src.title || '',
        url: src.url || '#',
        variant_id: src.variant_id,
        price_cents: bundlePrice,
        image: src.image || null,
        save_cents: Math.max(0, sum - bundlePrice)
      };
    }

    /* ============================================================
       STATE
       ============================================================ */
    var state = {
      current: 'intro',
      history: [],
      answers: {},
      // Per-product subscription choice, keyed by product key. Empty = every
      // item shows one-time pricing (matching the collection / product pages);
      // the customer can flip individual subscribable items to the 15% plan.
      subs: {},
      // Products the customer has unchecked on the result screen (key -> true).
      // Removed items stay visible (dimmed) but drop out of the total + cart.
      removed: {}
    };

    var SEGMENTS = ['vaginal', 'bladder', 'pregnancy', 'prevention'];

    function firstScreenFor(seg) {
      return { vaginal: 'v_focus', bladder: 'b_goal', pregnancy: 'p_stage', prevention: 'pr_ritual' }[seg];
    }

    /* progress % + label per screen */
    var PROGRESS = {
      intro: 0, segment: 12, segIntro: 25,
      v_focus: 42, v_method: 56, v_extras: 70,
      b_goal: 56, p_stage: 56, pr_ritual: 56,
      philosophy: 88, result: 100
    };
    function progressLabelFor(id) {
      var key = { intro: 'start', segment: 'focus', segIntro: 'segment',
        v_focus: 'refine', v_method: 'refine', v_extras: 'finish',
        b_goal: 'refine', p_stage: 'stage', pr_ritual: 'ritual',
        philosophy: 'almost', result: 'routine' }[id] || 'start';
      return t('progress.' + key, '');
    }

    /* ============================================================
       ROUTINE ENGINE  (claim-free, lifestyle/routine based)
       ============================================================ */
    function getRoutine() {
      var a = state.answers;
      var seg = a.segment;
      var items = [];
      var framingKey = 'general';

      if (seg === 'vaginal') {
        var focus = a.v_focus;        // reset | maintain | both
        var method = a.v_method;      // inwendig | oraal | either
        var extras = a.v_extras || [];
        if (focus === 'reset' || focus === 'both') {
          items.push(method === 'oraal' ? product('carvacrol', 'reset') : product('boric', 'reset'));
        }
        items.push(product('probiotics', 'daily'));
        if (extras.indexOf('cream') >= 0) items.push(product('cream', 'comfort'));
        if (extras.indexOf('shave') >= 0) items.push(product('shavebar', 'care'));
        framingKey = (focus === 'maintain') ? 'vaginal_maintain' : 'vaginal_active';

      } else if (seg === 'bladder') {
        var goal = a.b_goal;          // acute | prevent | both
        if (goal === 'prevent') {
          items.push(product('probiotics', 'daily'));
          items.push(product('uti', 'targeted'));
          framingKey = 'bladder_prevent';
        } else {
          items.push(product('uti', 'targeted'));
          items.push(product('probiotics', 'daily'));
          framingKey = (goal === 'both') ? 'bladder_both' : 'bladder_acute';
        }

      } else if (seg === 'pregnancy') {
        var stage = a.p_stage;        // trying | pregnant | postpartum
        items.push(product('prenatal', 'base'));
        items.push(product('probiotics', 'daily'));
        framingKey = (stage === 'trying') ? 'pregnancy_trying'
          : (stage === 'postpartum') ? 'pregnancy_postpartum' : 'pregnancy_pregnant';

      } else if (seg === 'prevention') {
        var ritual = a.pr_ritual || ['restore'];
        if (ritual.indexOf('restore') >= 0) items.push(product('probiotics', 'daily'));
        if (ritual.indexOf('shave') >= 0) items.push(product('shavebar', 'care'));
        if (items.length === 0) items.push(product('probiotics', 'daily'));
        framingKey = 'prevention';
      } else {
        items.push(product('probiotics', 'daily'));
      }

      // Drop products that don't resolve to a real, available variant
      // (e.g. Prepare Prenatal while it is still hidden from customers).
      items = items.filter(function (i) { return i && i.variant_id; });

      var key = items.map(function (i) { return i.key; }).sort().join('+');
      return {
        segment: seg,
        items: items,
        framingKey: framingKey,
        bundle: bundleInfo(key)
      };
    }

    function priceOf(item) {
      if (item.sub && state.subs[item.key]) return item.price_subscribe_cents;
      return item.price_cents;
    }
    function isSelected(item) {
      return !state.removed[item.key];
    }
    function routineTotal(routine) {
      return routine.items.reduce(function (t2, i) {
        return isSelected(i) ? t2 + priceOf(i) : t2;
      }, 0);
    }
    function oneTimeSum(routine) {
      return routine.items.reduce(function (t2, i) { return t2 + i.price_cents; }, 0);
    }

    /* ============================================================
       SHARED RENDER PIECES
       ============================================================ */
    function eyebrowText(label) {
      var step = questionStep();
      if (!step || !label) return label || '';
      var prefix = (step < 10 ? '0' + step : '' + step) + ' · ';
      return prefix + label;
    }
    function questionStep() {
      var path = state.history.concat([state.current]);
      var qScreens = ['segment', 'v_focus', 'v_method', 'v_extras', 'b_goal', 'p_stage', 'pr_ritual'];
      var n = 0;
      for (var i = 0; i < path.length; i++) {
        if (qScreens.indexOf(path[i]) >= 0) n++;
      }
      return n;
    }

    function opt(key, value, next, num, label, sub) {
      return ''
        + '<button class="option option-2line" data-sq-answer="' + key + '|' + value + '|' + next + '">'
        +   '<span class="option-num">' + num + '</span>'
        +   '<span class="option-text">'
        +     '<span class="option-title">' + label + '</span>'
        +     (sub ? '<span class="option-sub">' + sub + '</span>' : '')
        +   '</span>'
        + '</button>';
    }

    function renderMultiOptions(key, options, preselect) {
      if (!state.answers[key]) state.answers[key] = preselect ? preselect.slice() : [];
      var html = '<div class="options">';
      options.forEach(function (o, i) {
        var sel = state.answers[key].indexOf(o.value) >= 0 ? ' selected' : '';
        html += ''
          + '<button class="option option-2line multi' + sel + '" data-sq-multi="' + key + '" data-sq-multi-value="' + o.value + '">'
          +   '<span class="option-num">' + String(i + 1).padStart(2, '0') + '</span>'
          +   '<span class="option-text">'
          +     '<span class="option-title">' + o.label + '</span>'
          +     (o.sub ? '<span class="option-sub">' + o.sub + '</span>' : '')
          +   '</span>'
          + '</button>';
      });
      html += '</div>';
      return html;
    }
    function renderMultiActions(key, next, optional) {
      var count = (state.answers[key] || []).length;
      var disabled = (!optional && count === 0) ? ' disabled' : '';
      return ''
        + '<div class="actions">'
        +   '<button class="back" data-sq-back>' + t('common.back', 'Terug') + '</button>'
        +   '<button class="cta" data-sq-multi-continue="' + next + '"' + disabled + '>' + t('common.continue', 'Verder →') + '</button>'
        + '</div>';
    }
    function toggleMulti(key, value) {
      if (!state.answers[key]) state.answers[key] = [];
      var idx = state.answers[key].indexOf(value);
      if (idx >= 0) state.answers[key].splice(idx, 1);
      else state.answers[key].push(value);
      root.querySelectorAll('[data-sq-multi="' + key + '"]').forEach(function (btn) {
        btn.classList.toggle('selected', state.answers[key].indexOf(btn.getAttribute('data-sq-multi-value')) >= 0);
      });
    }

    function backActions() {
      return '<div class="actions"><button class="back" data-sq-back>' + t('common.back', 'Terug') + '</button></div>';
    }

    /* ============================================================
       SCREENS
       ============================================================ */
    var screens = {
      intro: function () { return ''
        + '<div class="screen intro">'
        +   (t('intro.tag') ? '<div class="intro-tag">' + t('intro.tag') + '</div>' : '')
        +   '<h1 class="headline">' + t('intro.headline_1') + '<br><em>' + t('intro.headline_2') + '</em></h1>'
        +   '<p class="intro-sub">' + t('intro.sub') + '</p>'
        +   '<button class="cta" data-sq-go="segment">' + t('intro.cta') + '</button>'
        +   (t('intro.trust') ? '<div class="intro-trust">' + t('intro.trust') + '</div>' : '')
        + '</div>';
      },

      segment: function () { return ''
        + '<div class="screen">'
        +   '<div class="eyebrow">' + eyebrowText(t('segment.eyebrow')) + '</div>'
        +   '<h1 class="headline">' + t('segment.headline') + '</h1>'
        +   '<p class="microcopy">' + t('segment.microcopy') + '</p>'
        +   '<div class="options">'
        +     opt('segment', 'vaginal',    'segIntro', '01', t('segment.opt_vaginal'),    t('segment.sub_vaginal'))
        +     opt('segment', 'bladder',    'segIntro', '02', t('segment.opt_bladder'),    t('segment.sub_bladder'))
        +     opt('segment', 'pregnancy',  'segIntro', '03', t('segment.opt_pregnancy'),  t('segment.sub_pregnancy'))
        +     opt('segment', 'prevention', 'segIntro', '04', t('segment.opt_prevention'), t('segment.sub_prevention'))
        +   '</div>'
        +   backActions()
        + '</div>';
      },

      segIntro: function () {
        var seg = state.answers.segment;
        var chips = listOf('seg.' + seg + '.chips');
        return ''
          + '<div class="screen seg-intro seg-' + seg + '">'
          +   '<div class="eyebrow">' + t('seg.' + seg + '.label') + '</div>'
          +   '<h1 class="headline">' + t('seg.' + seg + '.headline') + '</h1>'
          +   '<div class="seg-body">' + t('seg.' + seg + '.body') + '</div>'
          +   (chips.length ? '<div class="chips">' + chips.map(function (c) { return '<span class="chip">' + c + '</span>'; }).join('') + '</div>' : '')
          +   '<div class="actions">'
          +     '<button class="back" data-sq-back>' + t('common.back', 'Terug') + '</button>'
          +     '<button class="cta" data-sq-go="' + firstScreenFor(seg) + '">' + t('common.continue', 'Verder →') + '</button>'
          +   '</div>'
          + '</div>';
      },

      /* ---- VAGINAAL ---- */
      v_focus: function () { return ''
        + '<div class="screen">'
        +   '<div class="eyebrow">' + eyebrowText(t('vfocus.eyebrow')) + '</div>'
        +   '<h1 class="headline">' + t('vfocus.headline') + '</h1>'
        +   '<p class="microcopy">' + t('vfocus.microcopy') + '</p>'
        +   '<div class="options">'
        +     opt('v_focus', 'reset',    'v_method', '01', t('vfocus.opt_reset'),    t('vfocus.sub_reset'))
        +     opt('v_focus', 'maintain', 'v_extras', '02', t('vfocus.opt_maintain'), t('vfocus.sub_maintain'))
        +     opt('v_focus', 'both',     'v_method', '03', t('vfocus.opt_both'),     t('vfocus.sub_both'))
        +   '</div>'
        +   backActions()
        + '</div>';
      },

      v_method: function () { return ''
        + '<div class="screen">'
        +   '<div class="eyebrow">' + eyebrowText(t('vmethod.eyebrow')) + '</div>'
        +   '<h1 class="headline">' + t('vmethod.headline') + '</h1>'
        +   '<p class="microcopy">' + t('vmethod.microcopy') + '</p>'
        +   '<div class="options">'
        +     opt('v_method', 'inwendig', 'v_extras', '01', t('vmethod.opt_inwendig'), t('vmethod.sub_inwendig'))
        +     opt('v_method', 'oraal',    'v_extras', '02', t('vmethod.opt_oraal'),    t('vmethod.sub_oraal'))
        +     opt('v_method', 'either',   'v_extras', '03', t('vmethod.opt_either'),   t('vmethod.sub_either'))
        +   '</div>'
        +   backActions()
        + '</div>';
      },

      v_extras: function () { return ''
        + '<div class="screen">'
        +   '<div class="eyebrow">' + eyebrowText(t('vextras.eyebrow')) + '</div>'
        +   '<h1 class="headline">' + t('vextras.headline') + '</h1>'
        +   '<p class="microcopy">' + t('vextras.microcopy') + '</p>'
        +   renderMultiOptions('v_extras', [
              { value: 'cream', label: t('vextras.opt_cream'), sub: t('vextras.sub_cream') },
              { value: 'shave', label: t('vextras.opt_shave'), sub: t('vextras.sub_shave') }
            ])
        +   renderMultiActions('v_extras', 'philosophy', true)
        + '</div>';
      },

      /* ---- BLAAS ---- */
      b_goal: function () { return ''
        + '<div class="screen">'
        +   '<div class="eyebrow">' + eyebrowText(t('bgoal.eyebrow')) + '</div>'
        +   '<h1 class="headline">' + t('bgoal.headline') + '</h1>'
        +   '<p class="microcopy">' + t('bgoal.microcopy') + '</p>'
        +   '<div class="options">'
        +     opt('b_goal', 'acute',   'philosophy', '01', t('bgoal.opt_acute'),   t('bgoal.sub_acute'))
        +     opt('b_goal', 'prevent', 'philosophy', '02', t('bgoal.opt_prevent'), t('bgoal.sub_prevent'))
        +     opt('b_goal', 'both',    'philosophy', '03', t('bgoal.opt_both'),    t('bgoal.sub_both'))
        +   '</div>'
        +   backActions()
        + '</div>';
      },

      /* ---- ZWANGERSCHAP ---- */
      p_stage: function () { return ''
        + '<div class="screen">'
        +   '<div class="eyebrow">' + eyebrowText(t('pstage.eyebrow')) + '</div>'
        +   '<h1 class="headline">' + t('pstage.headline') + '</h1>'
        +   '<p class="microcopy">' + t('pstage.microcopy') + '</p>'
        +   '<div class="options">'
        +     opt('p_stage', 'trying',     'philosophy', '01', t('pstage.opt_trying'),     t('pstage.sub_trying'))
        +     opt('p_stage', 'pregnant',   'philosophy', '02', t('pstage.opt_pregnant'),   t('pstage.sub_pregnant'))
        +     opt('p_stage', 'postpartum', 'philosophy', '03', t('pstage.opt_postpartum'), t('pstage.sub_postpartum'))
        +   '</div>'
        +   backActions()
        + '</div>';
      },

      /* ---- PREVENTIE ---- */
      pr_ritual: function () { return ''
        + '<div class="screen">'
        +   '<div class="eyebrow">' + eyebrowText(t('pritual.eyebrow')) + '</div>'
        +   '<h1 class="headline">' + t('pritual.headline') + '</h1>'
        +   '<p class="microcopy">' + t('pritual.microcopy') + '</p>'
        +   renderMultiOptions('pr_ritual', [
              { value: 'restore', label: t('pritual.opt_restore'), sub: t('pritual.sub_restore') },
              { value: 'shave',   label: t('pritual.opt_shave'),   sub: t('pritual.sub_shave') }
            ], ['restore'])
        +   renderMultiActions('pr_ritual', 'philosophy', true)
        + '</div>';
      },

      /* ---- FILOSOFIE / ABONNEMENT ---- */
      philosophy: function () {
        var routine = getRoutine();
        var hasSub = routine.items.some(function (i) { return i.sub; });
        var body = hasSub ? t('philosophy.body_sub') : t('philosophy.body_nosub');
        return ''
          + '<div class="screen interstitial">'
          +   '<div class="eyebrow">' + t('philosophy.eyebrow') + '</div>'
          +   '<h1 class="headline">' + t('philosophy.headline') + '</h1>'
          +   '<div class="interstitial-body">' + body + '</div>'
          +   '<button class="cta" data-sq-go="result">' + t('philosophy.cta') + '</button>'
          + '</div>';
      },

      /* ---- RESULTAAT ---- */
      result: function () {
        var routine = getRoutine();
        var segLabel = (t('seg.' + routine.segment + '.label') || '').toLowerCase();
        var total = routineTotal(routine);
        var selectedItems = routine.items.filter(isSelected);
        var subhead = (t('result.subhead_template') || '')
          .replace('%segment%', segLabel)
          .replace('%framing%', framing(routine.framingKey));

        var cards = routine.items.map(function (item) {
          var role = t('result.step_' + item.roleKey, '');
          var cardOpen = '<div class="routine-card' + (isSelected(item) ? '' : ' deselected')
            + '" data-sq-card="' + item.key + '" role="button" tabindex="0"'
            + ' aria-pressed="' + (isSelected(item) ? 'true' : 'false') + '">'
            + '<span class="card-check" aria-hidden="true"></span>';
          if (item.sub) {
            return ''
              + cardOpen
              +   '<div class="step-label">' + role + '</div>'
              +   '<div class="product-name">' + item.name + '</div>'
              +   (item.desc ? '<div class="product-desc">' + item.desc + '</div>' : '')
              +   '<div class="sub-toggle">'
              +     '<button type="button" class="' + (state.subs[item.key] ? 'active' : '') + '" data-sq-sub="' + item.key + '|1">'
              +       '<span class="sub-label">' + t('result.sub_label_month') + '</span>'
              +       '<span class="sub-price">' + fmtPrice(item.price_subscribe_cents) + ' ' + t('result.sub_price_suffix', '') + '</span>'
              +     '</button>'
              +     '<button type="button" class="' + (!state.subs[item.key] ? 'active' : '') + '" data-sq-sub="' + item.key + '|0">'
              +       '<span class="sub-label">' + t('result.sub_label_once') + '</span>'
              +       '<span class="sub-price">' + fmtPrice(item.price_cents) + '</span>'
              +     '</button>'
              +   '</div>'
              +   (item.meta ? '<div class="product-meta">' + item.meta + '</div>' : '')
              + '</div>';
          }
          return ''
            + cardOpen
            +   '<div class="step-label">' + role + '</div>'
            +   '<div class="product-name">' + item.name + '</div>'
            +   (item.desc ? '<div class="product-desc">' + item.desc + '</div>' : '')
            +   '<div class="product-meta">' + fmtPrice(item.price_cents) + ' · ' + t('result.one_time_label', '') + (item.meta ? ' · ' + item.meta : '') + '</div>'
            + '</div>';
        }).join('');

        // Bundle suggestion follows the current selection: deselecting an item
        // can change which (if any) bundle matches the remaining products.
        var selKey = selectedItems.map(function (i) { return i.key; }).sort().join('+');
        var bundle = bundleInfo(selKey);
        var bundleHtml = '';
        if (bundle && bundle.save_cents > 0) {
          bundleHtml = ''
            + '<button class="bundle-cta" data-sq-add-bundle>'
            +   '<span><strong>' + (t('result.bundle_save_template') || '').replace('%save%', fmtPrice(bundle.save_cents)) + '</strong>'
            +     '<br><span class="dim ital">' + (t('result.bundle_swap_template') || '').replace('%name%', bundle.name) + '</span></span>'
            +   '<span class="bundle-cta-meta">→</span>'
            + '</button>';
        }

        var pregNote = (routine.segment === 'pregnancy' && t('result.preg_note'))
          ? '<p class="fine-note">' + t('result.preg_note') + '</p>' : '';

        var trust = listOf('result.trust');

        return ''
          + '<div class="screen result">'
          +   '<div class="eyebrow">' + t('result.eyebrow') + '</div>'
          +   '<h1 class="result-title">' + t('result.title') + '</h1>'
          +   '<p class="result-subhead">' + subhead + '</p>'
          +   cards
          +   (t('result.callout') ? '<div class="callout">' + t('result.callout') + '</div>' : '')
          +   '<div class="routine-total">'
          +     '<span class="total-label">' + t('result.total_label') + '</span>'
          +     '<span class="total-amount">' + fmtPrice(total) + '</span>'
          +   '</div>'
          +   '<button class="cta cta-large" data-sq-add-routine' + (selectedItems.length ? '' : ' disabled') + '>' + (t('result.add_cta_template') || '').replace('%total%', fmtPrice(total)) + '</button>'
          +   bundleHtml
          +   pregNote
          +   (trust.length ? '<div class="trust-strip">' + trust.map(function (x) { return '<span>' + x + '</span>'; }).join('') + '</div>' : '')
          + '</div>';
      }
    };

    /* ============================================================
       NAVIGATION
       ============================================================ */
    function go(screenId) {
      state.history.push(state.current);
      state.current = screenId;
      render();
    }
    function back() {
      if (state.history.length > 0) {
        state.current = state.history.pop();
        render();
      }
    }
    function answer(key, value, next) {
      state.answers[key] = value;
      go(next);
    }
    function setSub(key, value) {
      state.subs[key] = value;
      render(true);
    }
    function toggleCard(key) {
      if (state.removed[key]) delete state.removed[key];
      else state.removed[key] = true;
      render(true);
    }
    function restart() {
      state.current = 'intro';
      state.history = [];
      state.answers = {};
      state.subs = {};
      render();
    }

    /* ============================================================
       CART ADD
       ============================================================ */
    function addItemsToCart(items, btn) {
      if (!items.length) return;
      if (btn) btn.setAttribute('data-loading', 'true');
      return fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ items: items })
      }).then(function (r) {
        if (!r.ok) return r.text().then(function (txt) { throw new Error('cart/add failed: ' + r.status + ' ' + txt); });
        return r.json();
      }).then(function () {
        window.location.href = '/cart';
      }).catch(function (err) {
        console.error('[solvey-quiz] cart add failed', err);
        if (btn) {
          btn.removeAttribute('data-loading');
          btn.textContent = t('errors.cart_failed', 'Niet gelukt — probeer opnieuw');
        }
      });
    }

    function addRoutineToCart(btn) {
      var routine = getRoutine();
      var fallbackSp = parseInt(config.subscription_selling_plan_id, 10);
      var items = routine.items.filter(isSelected).map(function (i) {
        var line = { id: i.variant_id, quantity: 1 };
        if (i.sub && state.subs[i.key]) {
          var sp = i.sub_plan_id || fallbackSp;
          if (sp) line.selling_plan = sp;
        }
        return line;
      });
      if (!items.length) return;
      addItemsToCart(items, btn);
    }
    function addBundleToCart(btn) {
      var routine = getRoutine();
      if (!routine.bundle || !routine.bundle.variant_id) return;
      addItemsToCart([{ id: routine.bundle.variant_id, quantity: 1 }], btn);
    }

    /* ============================================================
       EVENTS
       ============================================================ */
    app.addEventListener('click', function (e) {
      var el = e.target.closest('[data-sq-go],[data-sq-back],[data-sq-answer],[data-sq-multi],[data-sq-multi-continue],[data-sq-sub],[data-sq-add-routine],[data-sq-add-bundle],[data-sq-card]');
      if (!el) return;
      if (el.hasAttribute('data-sq-go')) {
        go(el.getAttribute('data-sq-go'));
      } else if (el.hasAttribute('data-sq-back')) {
        back();
      } else if (el.hasAttribute('data-sq-answer')) {
        var parts = el.getAttribute('data-sq-answer').split('|');
        answer(parts[0], parts[1], parts[2]);
      } else if (el.hasAttribute('data-sq-multi')) {
        toggleMulti(el.getAttribute('data-sq-multi'), el.getAttribute('data-sq-multi-value'));
      } else if (el.hasAttribute('data-sq-multi-continue')) {
        if (!el.disabled) go(el.getAttribute('data-sq-multi-continue'));
      } else if (el.hasAttribute('data-sq-sub')) {
        var subParts = el.getAttribute('data-sq-sub').split('|');
        setSub(subParts[0], subParts[1] === '1');
      } else if (el.hasAttribute('data-sq-add-routine')) {
        addRoutineToCart(el);
      } else if (el.hasAttribute('data-sq-add-bundle')) {
        addBundleToCart(el);
      } else if (el.hasAttribute('data-sq-card')) {
        toggleCard(el.getAttribute('data-sq-card'));
      }
    });

    /* Keyboard support for the click-to-toggle routine cards (role="button"). */
    app.addEventListener('keydown', function (e) {
      if (e.key !== 'Enter' && e.key !== ' ' && e.key !== 'Spacebar') return;
      var card = e.target.closest('[data-sq-card]');
      if (!card || !app.contains(card)) return;
      e.preventDefault();
      toggleCard(card.getAttribute('data-sq-card'));
    });

    var restartBtn = root.querySelector('[data-sq-restart]');
    if (restartBtn) restartBtn.addEventListener('click', restart);

    /* ============================================================
       RENDER
       ============================================================ */
    // keepScroll=true re-renders in place (used for in-screen toggles like the
    // subscription switch and card (de)select) so the page does not jump to top.
    function render(keepScroll) {
      var screen = screens[state.current];
      if (!screen) {
        app.innerHTML = '<p>Screen not found: ' + state.current + '</p>';
        return;
      }
      app.innerHTML = screen();
      if (progressFill) progressFill.style.width = (PROGRESS[state.current] || 0) + '%';
      if (progressLabel) progressLabel.textContent = progressLabelFor(state.current);
      if (!keepScroll) window.scrollTo({ top: 0, behavior: 'smooth' });
    }
    render();

    /* Re-localise on Weglot language switch */
    function relocalize() { cp = buildCopy(); render(); }
    if (window.Weglot && typeof window.Weglot.on === 'function') {
      try {
        window.Weglot.on('languageChanged', relocalize);
        window.Weglot.on('initialized', relocalize);
      } catch (e) {}
    }
  }

  function initAll() {
    document.querySelectorAll('[data-sq-root]').forEach(init);
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }
  document.addEventListener('shopify:section:load', initAll);
})();
